<script lang="ts">
	// @ts-nocheck
	import { onMount } from 'svelte';
	import { Button } from '$lib/components/ui/button';
	import { Input } from '$lib/components/ui/input';
	import { Label } from '$lib/components/ui/label';
	import * as Select from '$lib/components/ui/select';
	import { Textarea } from '$lib/components/ui/textarea';
	import * as Alert from '$lib/components/ui/alert';
	import * as Accordion from '$lib/components/ui/accordion';
	import * as Carousel from '$lib/components/ui/carousel';
	import { fly } from 'svelte/transition';
	import { Check, CheckCircle, Loader2, Star, MapPin, Heart } from 'lucide-svelte';
	import Seo from '$lib/components/SEO.svelte';

	import heroImage from '$lib/assets/enhanced/most-iconic.jpg?enhanced';
	import walksImage from '$lib/assets/enhanced/devil-matt.jpg?enhanced';
	import brunyImage from '$lib/assets/highlights/bruny-island-1.jpg?enhanced';
	import wineglassImage from '$lib/assets/enhanced/wineglass-bay.jpg?enhanced';
	import scenicImage from '$lib/assets/highlights/scenic.jpg?enhanced';
	import calendarIllustration from '$lib/assets/illustrations/calendar.png?enhanced';
	import planningIllustration from '$lib/assets/illustrations/planning.png?enhanced';
	import mapIllustration from '$lib/assets/illustrations/map.png?enhanced';
	import mountainIllustration from '$lib/assets/illustrations/mountain.png?enhanced';

	let animate = $state(false);

	onMount(() => {
		setTimeout(() => {
			window.scrollTo(0, 0);
		}, 200);
		animate = true;
	});

	function handleAnchorClick(event) {
		event.preventDefault();
		const link = event.currentTarget;
		const anchorId = new URL(link.href).hash.replace('#', '');
		const anchor = document.getElementById(anchorId);
		if (anchor) {
			window.scrollTo({
				top: anchor.offsetTop,
				behavior: 'smooth'
			});
		}
	}

	// Form state
	let submitted = $state(false);
	let loading = $state(false);
	let firstName = $state('');
	let lastName = $state('');
	let email = $state('');
	let platform = $state('');
	let audienceSize = $state('');
	let niche = $state('');
	let why = $state('');
	let type = 'Creator Partner Application';

	const audienceSizeOptions = [
		{ value: '0-10000', label: 'Up to 10,000' },
		{ value: '10000-25000', label: '10,000 – 25,000' },
		{ value: '25000-50000', label: '25,000 – 50,000' },
		{ value: '50000+', label: '50,000+' }
	];

	const nicheOptions = [
		{ value: 'travel', label: 'Travel' },
		{ value: 'outdoor-adventure', label: 'Outdoor · Adventure' },
		{ value: 'food-lifestyle', label: 'Food · Lifestyle' },
		{ value: 'photography', label: 'Photography' },
		{ value: 'videography-film', label: 'Videography · Film' },
		{ value: 'other', label: 'Other' }
	];

	const audienceSizeTrigger = $derived(
		audienceSizeOptions.find((o) => o.value === audienceSize)?.label ?? 'Select audience size'
	);

	const nicheTrigger = $derived(
		nicheOptions.find((o) => o.value === niche)?.label ?? 'Select your niche'
	);

	function resetForm() {
		firstName = '';
		lastName = '';
		email = '';
		platform = '';
		audienceSize = '';
		niche = '';
		why = '';
	}

	async function handleSubmit(e: Event) {
		e.preventDefault();
		loading = true;
		const formData = {
			name: `${firstName} ${lastName}`,
			email,
			platform,
			audienceSize,
			niche,
			why,
			type
		};

		try {
			const response = await fetch('/email/send-email', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(formData)
			});

			if (!response.ok) throw new Error('Failed to send email');

			const result = await response.json();

			if (result.success) {
				resetForm();
				submitted = true;
			} else {
				throw new Error(result.error || 'Failed to send email');
			}
		} catch (error) {
			console.error('Error sending email:', error);
		} finally {
			loading = false;
		}
	}

	const steps = [
		{
			number: '01',
			title: 'Apply below',
			body: 'Fill in the short form — your handle, audience size, niche, and why Tasmania. Takes 3 minutes.',
			illustration: 'map'
		},
		{
			number: '02',
			title: 'We review and match',
			body: "We'll look at your content style and audience fit, then match you with the tours that will work best.",
			illustration: 'planning'
		},
		{
			number: '03',
			title: 'Book your dates',
			body: "We'll confirm your complimentary tour dates, route, and any bespoke itinerary details.",
			illustration: 'calendar'
		},
		{
			number: '04',
			title: 'Create and share',
			body: "Show up, explore Tasmania, and post the content you'd naturally create. That's it.",
			illustration: 'mountain'
		}
	];

	const faqItems = [
		{
			question: 'Do I need a minimum follower count?',
			answer:
				'No — our Explorer tier welcomes creators with up to 10,000 followers. What matters more than raw numbers is engagement quality and content fit.'
		},
		{
			question: 'Can I choose which tour I go on?',
			answer:
				"Yes — we'll discuss options when we confirm your partnership, and we can tailor the experience to best suit your content style."
		},
		{
			question: 'Does the offer cover flights or accommodation?',
			answer:
				"The complimentary offer covers the tour itself. Accommodation is sometimes available for multi-day Pioneer-tier partnerships — we'll confirm this individually."
		},
		{
			question: 'Who owns the content I create?',
			answer:
				'You do. We ask for usage rights to share your content on our channels and website — but your content remains yours entirely.'
		},
		{
			question: 'How long after the tour do I need to post?',
			answer:
				'We ask that posts go live within 30 days of your tour. For multi-day trips, a staggered posting schedule is fine — just let us know your plan.'
		},
		{
			question: "What's the best time of year to come?",
			answer:
				"October through April gives you the best weather and longest daylight hours. That said, Tasmania's moody winters are beautiful for a certain kind of content too."
		}
	];
</script>

<Seo
	title="Content Creator Partner Program - Tassie Tours"
	description="We're offering free tours to photographers, videographers, and content creators in exchange for authentic content about Tasmania. Apply to join the Tassie Tours Creator Partner Program."
	keywords="Tasmania content creator, travel creator Tasmania, photographer tours Tasmania, videographer Tasmania, Tassie Tours creator program"
	siteName="Tassie Tours"
	imageURL="/images/hero-carousel-1.jpg"
	logo="/src/lib/assets/logo/logo-black.png"
	author="Tassie Tours"
	type="website"
	name="Tassie Tours"
	index={true}
	twitter={true}
	openGraph={true}
	schemaOrg={true}
/>

<!-- Hero -->
<section class="relative mx-auto flex h-[600px] flex-col items-center justify-center">
	<div
		class="absolute bottom-0 left-0 z-10 h-48 w-full"
		style="clip-path: polygon(26% 96%,100% 73%,100% 100%,0 100%,0 88%);"
	>
		<div class="h-full bg-white"></div>
	</div>
	<enhanced:img
		src={heroImage}
		alt="Content Creator Partner Program - Tassie Tours"
		class="absolute inset-0 h-full w-full object-cover"
		sizes="100vw"
		fetchpriority="high"
	/>
	<div class="absolute inset-0 bg-foreground/50"></div>
	<div
		class={`z-10 mx-auto flex max-w-7xl flex-col items-center justify-center px-4 text-center ${
			animate ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
		}`}
		style="transition: all 0.9s cubic-bezier(0.17, 0.55, 0.55, 1) 0.5s"
	>
		<span
			class="mb-4 inline-block rounded-full bg-[#ffd451] px-4 py-1.5 text-sm font-semibold text-foreground"
		>
			Content Creator Partners
		</span>
		<h1
			class="max-w-4xl font-heading text-4xl font-bold tracking-tight text-background sm:text-5xl md:text-6xl"
		>
			We're Seeking Creator Partners to Showcase Tasmania
		</h1>
		<div class="mx-auto mt-6 flex max-w-2xl items-center">
			<p class="text-md mx-auto text-center font-semibold text-background lg:text-lg">
				We're offering free tours to photographers, videographers, and content creators in exchange
				for content that captures what words can't. Wild coasts. Ancient forests. Food worth flying
				for.
			</p>
		</div>
		<div class="mx-auto flex flex-col items-center justify-center gap-4 sm:flex-row">
			<Button
				data-sveltekit-noscroll
				href="#apply"
				onclick={handleAnchorClick}
				class="pointer-events-auto mx-auto mt-8 rounded-full bg-tt-red px-6 font-semibold hover:bg-tt-red/90 lg:text-lg"
				>Apply to partner</Button
			>
			<Button
				data-sveltekit-noscroll
				onclick={handleAnchorClick}
				variant="outline"
				href="#tiers"
				class="pointer-events-auto mx-auto mt-8 rounded-full border-2 bg-transparent px-6 font-semibold text-background lg:text-lg"
				>View creator tiers</Button
			>
		</div>
	</div>
</section>

<!-- Why Partner With Us -->
<div class="bg-white pt-12">
	<div class="mx-auto max-w-7xl px-4">
		<div class="mx-auto flex max-w-5xl justify-center text-center">
			<div class="text-base leading-7 text-gray-700">
				<h2 class="mt-8 scroll-m-20 font-heading text-3xl font-semibold tracking-tight">
					Real Tasmania. Remarkable content.
				</h2>
				<p class="mt-4 leading-7">
					Tasmania isn't like anywhere else in Australia and your audience knows it. We give you
					unscripted, guide-led access to landscapes that stop scrolls. In return, we get content
					that does what no ad ever could.
				</p>
			</div>
		</div>

		<!-- Stat callouts -->
		<div class="mx-auto mt-16 grid w-full max-w-3xl grid-cols-1 gap-8 pb-20 sm:grid-cols-3">
			<div class="flex flex-col items-center text-center">
				<div class="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#194139]/10">
					<Star class="h-7 w-7 text-[#194139]" />
				</div>
				<p class="font-heading text-3xl font-bold">2 000+</p>
				<p class="mt-1 text-sm text-gray-600">five-star reviews from real travellers</p>
			</div>
			<div class="flex flex-col items-center text-center">
				<div class="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#194139]/10">
					<MapPin class="h-7 w-7 text-[#194139]" />
				</div>
				<p class="font-heading text-3xl font-bold">15+</p>
				<p class="mt-1 text-sm text-gray-600">unique tour routes across the island</p>
			</div>
			<div class="flex flex-col items-center text-center">
				<div class="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#194139]/10">
					<Heart class="h-7 w-7 text-[#194139]" />
				</div>
				<p class="font-heading text-3xl font-bold">100%</p>
				<p class="mt-1 text-sm text-gray-600">family-owned and locally operated</p>
			</div>
		</div>
	</div>
</div>

<!-- Creator Tiers -->
<section id="tiers" class="relative bg-[#194139] pb-32 pt-24">
	<div
		class="absolute -top-48 left-0 z-10 h-48 w-full"
		style="clip-path: polygon(27% 94%,66% 86%,100% 91%,100% 100%,0 100%,0 88%);"
	>
		<div class="h-full bg-[#194139]"></div>
	</div>
	<div
		class="absolute bottom-0 left-0 z-10 h-48 w-full"
		style="clip-path: polygon(27% 94%,66% 86%,100% 91%,100% 100%,0 100%,0 88%);"
	>
		<div class="h-full bg-white"></div>
	</div>
	<div class="mx-auto max-w-7xl px-4">
		<div class="text-center text-white">
			<h2 class="font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
				What's on offer and what we ask
			</h2>
		</div>

		<div class="mt-16 grid grid-cols-1 gap-8 sm:grid-cols-2 xl:grid-cols-4">
			<!-- Explorer -->
			<div class="overflow-hidden rounded-2xl bg-white shadow-lg">
				<div class="relative h-48">
					<enhanced:img
						src={walksImage}
						alt="Explorer creator tier"
						class="h-full w-full object-cover"
						sizes="(min-width: 1280px) 25vw, (min-width: 640px) 50vw, 100vw"
						loading="lazy"
					/>
					<div class="absolute inset-0 bg-foreground/30"></div>
					<div class="absolute bottom-4 left-4">
						<p class="font-heading text-2xl font-bold text-white">Explorer</p>
						<p class="text-sm text-white/90">Up to 10,000 followers</p>
					</div>
				</div>
				<div class="flex h-[calc(100%-12rem)] flex-col p-6">
					<div class="mb-4 flex-1">
						<h3
							class="mb-3 font-heading text-sm font-semibold uppercase tracking-wide text-[#194139]"
						>
							What you receive
						</h3>
						<ul class="space-y-2">
							<li class="flex items-start gap-2 text-sm">
								<Check class="mt-0.5 h-4 w-4 shrink-0 text-tt-red" />
								Complimentary tour (1–2 days, negotiated based on previous content and identifiable talent)
							</li>
							<li class="flex items-start gap-2 text-sm">
								<Check class="mt-0.5 h-4 w-4 shrink-0 text-tt-red" />
								Guided experience for 1 person
							</li>
							<li class="flex items-start gap-2 text-sm">
								<Check class="mt-0.5 h-4 w-4 shrink-0 text-tt-red" />
								Co-promotion on our channels
							</li>
						</ul>
					</div>
					<div class="border-t border-gray-100 pt-4">
						<h3
							class="mb-3 font-heading text-sm font-semibold uppercase tracking-wide text-gray-500"
						>
							What we ask
						</h3>
						<ul class="space-y-2">
							<li class="flex items-start gap-2 text-sm text-gray-600">
								<span class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400"></span>
								1 × Reel tagging @tassietours_tasmania
							</li>
							<li class="flex items-start gap-2 text-sm text-gray-600">
								<span class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400"></span>
								3 × Stories during the tour
							</li>
							<li class="flex items-start gap-2 text-sm text-gray-600">
								<span class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400"></span>
								Usage rights for website and social media
							</li>
						</ul>
						<div class="my-3 flex items-center gap-3">
							<span class="h-px flex-1 bg-gray-200"></span>
							<span class="font-heading text-xs font-semibold uppercase tracking-wide text-gray-400"
								>Or</span
							>
							<span class="h-px flex-1 bg-gray-200"></span>
						</div>
						<ul class="space-y-2">
							<li class="flex items-start gap-2 text-sm text-gray-600">
								<span class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400"></span>
								1 × Reel supplied for Tassie Tours' own use only (negotiated on individual basis)
							</li>
						</ul>
					</div>
					<Button
						href="#apply"
						onclick={handleAnchorClick}
						data-sveltekit-noscroll
						class="mt-6 w-full rounded-full bg-tt-red font-semibold hover:bg-tt-red/90"
					>
						Apply now
					</Button>
				</div>
			</div>

			<!-- Adventurer -->
			<div class="overflow-hidden rounded-2xl bg-white shadow-lg">
				<div class="relative h-48">
					<enhanced:img
						src={brunyImage}
						alt="Adventurer creator tier"
						class="h-full w-full object-cover"
						sizes="(min-width: 1280px) 25vw, (min-width: 640px) 50vw, 100vw"
						loading="lazy"
					/>
					<div class="absolute inset-0 bg-foreground/30"></div>
					<div class="absolute bottom-4 left-4">
						<p class="font-heading text-2xl font-bold text-white">Adventurer</p>
						<p class="text-sm text-white/90">10,000 – 25,000 followers</p>
					</div>
				</div>
				<div class="flex h-[calc(100%-12rem)] flex-col p-6">
					<div class="mb-4 flex-1">
						<h3
							class="mb-3 font-heading text-sm font-semibold uppercase tracking-wide text-[#194139]"
						>
							What you receive
						</h3>
						<ul class="space-y-2">
							<li class="flex items-start gap-2 text-sm">
								<Check class="mt-0.5 h-4 w-4 shrink-0 text-tt-red" />
								Up to 3-day complimentary tour
							</li>
							<li class="flex items-start gap-2 text-sm">
								<Check class="mt-0.5 h-4 w-4 shrink-0 text-tt-red" />
								Guided experience for 1 person
							</li>
							<li class="flex items-start gap-2 text-sm">
								<Check class="mt-0.5 h-4 w-4 shrink-0 text-tt-red" />
								Co-promotion on our channels
							</li>
						</ul>
					</div>
					<div class="border-t border-gray-100 pt-4">
						<h3
							class="mb-3 font-heading text-sm font-semibold uppercase tracking-wide text-gray-500"
						>
							What we ask
						</h3>
						<ul class="space-y-2">
							<li class="flex items-start gap-2 text-sm text-gray-600">
								<span class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400"></span>
								2 × Reels tagging @tassietours_tasmania
							</li>
							<li class="flex items-start gap-2 text-sm text-gray-600">
								<span class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400"></span>
								5 × Stories during the experience
							</li>
							<li class="flex items-start gap-2 text-sm text-gray-600">
								<span class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400"></span>
								3 × tagged in-feed photos
							</li>
							<li class="flex items-start gap-2 text-sm text-gray-600">
								<span class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400"></span>
								Usage rights for website and social media
							</li>
						</ul>
					</div>
					<Button
						href="#apply"
						onclick={handleAnchorClick}
						data-sveltekit-noscroll
						class="mt-6 w-full rounded-full bg-tt-red font-semibold hover:bg-tt-red/90"
					>
						Apply now
					</Button>
				</div>
			</div>

			<!-- Trailblazer -->
			<div class="overflow-hidden rounded-2xl bg-white shadow-xl ring-2 ring-[#ffd451]">
				<div class="relative h-48">
					<enhanced:img
						src={wineglassImage}
						alt="Trailblazer creator tier"
						class="h-full w-full object-cover"
						sizes="(min-width: 1280px) 25vw, (min-width: 640px) 50vw, 100vw"
						loading="lazy"
					/>
					<div class="absolute inset-0 bg-foreground/30"></div>
					<div class="absolute bottom-4 left-4">
						<p class="font-heading text-2xl font-bold text-white">Trailblazer</p>
						<p class="text-sm text-white/90">25,000 – 50,000 followers</p>
					</div>
				</div>
				<div class="flex h-[calc(100%-12rem)] flex-col p-6">
					<div class="mb-4 flex-1">
						<h3
							class="mb-3 font-heading text-sm font-semibold uppercase tracking-wide text-[#194139]"
						>
							What you receive
						</h3>
						<ul class="space-y-2">
							<li class="flex items-start gap-2 text-sm">
								<Check class="mt-0.5 h-4 w-4 shrink-0 text-tt-red" />
								Free 6-day complimentary tour
							</li>
							<li class="flex items-start gap-2 text-sm">
								<Check class="mt-0.5 h-4 w-4 shrink-0 text-tt-red" />
								Guided experience for 2 people
							</li>
							<li class="flex items-start gap-2 text-sm">
								<Check class="mt-0.5 h-4 w-4 shrink-0 text-tt-red" />
								Feature in our email campaign
							</li>
							<li class="flex items-start gap-2 text-sm">
								<Check class="mt-0.5 h-4 w-4 shrink-0 text-tt-red" />
								Co-promotion on all channels
							</li>
						</ul>
					</div>
					<div class="border-t border-gray-100 pt-4">
						<h3
							class="mb-3 font-heading text-sm font-semibold uppercase tracking-wide text-gray-500"
						>
							What we ask
						</h3>
						<ul class="space-y-2">
							<li class="flex items-start gap-2 text-sm text-gray-600">
								<span class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400"></span>
								3 × Reels tagging @tassietours_tasmania
							</li>
							<li class="flex items-start gap-2 text-sm text-gray-600">
								<span class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400"></span>
								8 × Stories throughout the experience
							</li>
							<li class="flex items-start gap-2 text-sm text-gray-600">
								<span class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400"></span>
								5 × tagged in-feed posts or photos
							</li>
							<li class="flex items-start gap-2 text-sm text-gray-600">
								<span class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400"></span>
								Full usage rights
							</li>
						</ul>
					</div>
					<Button
						href="#apply"
						onclick={handleAnchorClick}
						data-sveltekit-noscroll
						class="mt-6 w-full rounded-full bg-tt-red font-semibold hover:bg-tt-red/90"
					>
						Apply now
					</Button>
				</div>
			</div>

			<!-- Pioneer -->
			<div class="overflow-hidden rounded-2xl bg-white shadow-lg">
				<div class="relative h-48">
					<enhanced:img
						src={scenicImage}
						alt="Pioneer creator tier"
						class="h-full w-full object-cover"
						sizes="(min-width: 1280px) 25vw, (min-width: 640px) 50vw, 100vw"
						loading="lazy"
					/>
					<div class="absolute inset-0 bg-foreground/30"></div>
					<div class="absolute bottom-4 left-4">
						<p class="font-heading text-2xl font-bold text-white">Pioneer</p>
						<p class="text-sm text-white/90">More than 50,000 followers</p>
					</div>
				</div>
				<div class="flex h-[calc(100%-12rem)] flex-col p-6">
					<div class="mb-4 flex-1">
						<h3
							class="mb-3 font-heading text-sm font-semibold uppercase tracking-wide text-[#194139]"
						>
							What you receive
						</h3>
						<ul class="space-y-2">
							<li class="flex items-start gap-2 text-sm">
								<Check class="mt-0.5 h-4 w-4 shrink-0 text-tt-red" />
								Free 6-day tour
							</li>
							<li class="flex items-start gap-2 text-sm">
								<Check class="mt-0.5 h-4 w-4 shrink-0 text-tt-red" />
								Negotiated accommodation included
							</li>
							<li class="flex items-start gap-2 text-sm">
								<Check class="mt-0.5 h-4 w-4 shrink-0 text-tt-red" />
								Extra passenger included
							</li>
							<li class="flex items-start gap-2 text-sm">
								<Check class="mt-0.5 h-4 w-4 shrink-0 text-tt-red" />
								Priority booking and dedicated guide
							</li>
							<li class="flex items-start gap-2 text-sm">
								<Check class="mt-0.5 h-4 w-4 shrink-0 text-tt-red" />
								Feature across all Tassie Tours channels
							</li>
						</ul>
					</div>
					<div class="border-t border-gray-100 pt-4">
						<h3
							class="mb-3 font-heading text-sm font-semibold uppercase tracking-wide text-gray-500"
						>
							What we ask
						</h3>
						<ul class="space-y-2">
							<li class="flex items-start gap-2 text-sm text-gray-600">
								<span class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400"></span>
								5 × Reels or 1 dedicated travel video
							</li>
							<li class="flex items-start gap-2 text-sm text-gray-600">
								<span class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400"></span>
								10 × tagged in-feed posts across the tour
							</li>
							<li class="flex items-start gap-2 text-sm text-gray-600">
								<span class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400"></span>
								Daily Stories throughout
							</li>
							<li class="flex items-start gap-2 text-sm text-gray-600">
								<span class="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-400"></span>
								Full commercial usage rights
							</li>
						</ul>
					</div>
					<Button
						href="#apply"
						onclick={handleAnchorClick}
						data-sveltekit-noscroll
						class="mt-6 w-full rounded-full bg-tt-red font-semibold hover:bg-tt-red/90"
					>
						Apply now
					</Button>
				</div>
			</div>
		</div>
	</div>
</section>

<!-- How It Works -->
<div class="bg-white py-24">
	<div class="mx-auto max-w-7xl px-4">
		<div class="text-center">
			<h2 class="font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
				Simple from start to share
			</h2>
		</div>
		<div class="mx-auto mt-16 grid max-w-7xl grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-4">
			{#each steps as step}
				<div class="col-span-1 flex flex-col items-center text-center">
					{#if step.illustration === 'calendar'}
						<enhanced:img
							src={calendarIllustration}
							class="mx-auto h-auto w-28"
							alt="Apply step"
							sizes="112px"
							loading="lazy"
						/>
					{:else if step.illustration === 'planning'}
						<enhanced:img
							src={planningIllustration}
							class="mx-auto h-auto w-28"
							alt="Review step"
							sizes="112px"
							loading="lazy"
						/>
					{:else if step.illustration === 'map'}
						<enhanced:img
							src={mapIllustration}
							class="mx-auto h-auto w-28"
							alt="Book step"
							sizes="112px"
							loading="lazy"
						/>
					{:else if step.illustration === 'mountain'}
						<enhanced:img
							src={mountainIllustration}
							class="mx-auto h-auto w-28"
							alt="Create step"
							sizes="112px"
							loading="lazy"
						/>
					{/if}
					<p class="mt-4 font-heading text-4xl font-bold text-[#194139]/20">{step.number}</p>
					<h3 class="mt-2 font-heading text-lg font-semibold">{step.title}</h3>
					<p class="mt-2 text-sm text-gray-600">{step.body}</p>
				</div>
			{/each}
		</div>
	</div>
</div>

<!-- Info + FAQ (merged) -->
<div class="bg-white py-12 sm:pt-24">
	<div class="mx-auto max-w-7xl px-4">
		<div
			class="mx-auto grid max-w-2xl grid-cols-1 items-start gap-x-12 gap-y-16 sm:gap-y-24 lg:mx-0 lg:max-w-none lg:grid-cols-2"
		>
			<!-- Image carousel (order-2 pushes it visually right) -->
			<Carousel.Root class="relative order-2 flex h-full w-full">
				<Carousel.Content class="h-full">
					<Carousel.Item class="relative inset-0 h-full w-full object-cover">
						<div class="h-full w-full object-cover">
							<enhanced:img
								alt="Tasmania coastal walks"
								src={walksImage}
								class="block h-full w-full rounded-lg object-cover"
								loading="lazy"
								sizes="(min-width: 1024px) 50vw, 100vw"
							/>
						</div>
					</Carousel.Item>
					<Carousel.Item class="relative inset-0 h-full w-full object-cover">
						<div class="h-full w-full object-cover">
							<enhanced:img
								alt="Bruny Island Tasmania"
								src={brunyImage}
								class="block h-full w-full rounded-lg object-cover"
								loading="lazy"
								sizes="(min-width: 1024px) 50vw, 100vw"
							/>
						</div>
					</Carousel.Item>
					<Carousel.Item class="relative inset-0 h-full w-full object-cover">
						<div class="h-full w-full object-cover">
							<enhanced:img
								alt="Wineglass Bay Tasmania"
								src={wineglassImage}
								class="block h-full w-full rounded-lg object-cover"
								loading="lazy"
								sizes="(min-width: 1024px) 50vw, 100vw"
							/>
						</div>
					</Carousel.Item>
					<Carousel.Item class="relative inset-0 h-full w-full object-cover">
						<div class="h-full w-full object-cover">
							<enhanced:img
								alt="Scenic Tasmania"
								src={scenicImage}
								class="block h-full w-full rounded-lg object-cover"
								loading="lazy"
								sizes="(min-width: 1024px) 50vw, 100vw"
							/>
						</div>
					</Carousel.Item>
				</Carousel.Content>
				<Carousel.Previous />
				<Carousel.Next />
			</Carousel.Root>

			<!-- Left: ready to explore + checklist + FAQ -->
			<div>
				<div class="text-base leading-7 text-gray-700">
					<h2 class="font-heading text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
						Ready to explore Tasmania on us?
					</h2>
					<div class="max-w-xl">
						<p class="col-span-1 mt-6 leading-7">
							We review every application personally. If there's a fit, you'll hear back within 5
							business days. We're especially keen to hear from creators who shoot travel, outdoor
							adventure, food, and lifestyle content.
						</p>
						<ul class="mt-6 space-y-3">
							<li class="flex items-center gap-3">
								<Check class="h-5 w-5 shrink-0 text-tt-red" />
								<span>Free tour — no strings, no fees</span>
							</li>
							<li class="flex items-center gap-3">
								<Check class="h-5 w-5 shrink-0 text-tt-red" />
								<span>Content stays yours — we just get usage rights</span>
							</li>
							<li class="flex items-center gap-3">
								<Check class="h-5 w-5 shrink-0 text-tt-red" />
								<span>Genuine Tasmania experiences, not scripted tours</span>
							</li>
							<li class="flex items-center gap-3">
								<Check class="h-5 w-5 shrink-0 text-tt-red" />
								<span>Flexible scheduling around your calendar</span>
							</li>
							<li class="flex items-center gap-3">
								<Check class="h-5 w-5 shrink-0 text-tt-red" />
								<span>Weather is best October – April</span>
							</li>
						</ul>
						<div class="mt-6 w-full">
							<Accordion.Root class="w-full" type="single" value="item-1">
								{#each faqItems as item, i}
									<Accordion.Item value={`item-${i + 1}`}>
										<Accordion.Trigger class="w-full text-left font-heading text-lg font-bold"
											>{item.question}</Accordion.Trigger
										>
										<Accordion.Content class="w-full py-2 pl-4">
											{item.answer}
										</Accordion.Content>
									</Accordion.Item>
								{/each}
							</Accordion.Root>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div class="mx-auto mt-8 flex flex-col items-center justify-center gap-4 lg:flex-row">
			<Button
				data-sveltekit-noscroll
				href="#apply"
				onclick={handleAnchorClick}
				class="pointer-events-auto rounded-full bg-tt-red px-6 font-semibold hover:bg-tt-red/90 lg:text-lg"
				>Apply to partner
			</Button>
			<Button
				data-sveltekit-noscroll
				variant="outline"
				href="/contact"
				class="pointer-events-auto rounded-full border-2 bg-transparent px-6 font-semibold lg:text-lg"
				>Get in Touch</Button
			>
		</div>
	</div>
</div>

<!-- Application Form -->
<section class="mx-auto px-4 pb-12 pt-24" id="apply">
	<div class="mx-auto max-w-7xl">
		<div class="mx-auto lg:mx-0">
			<h2
				class="text-center font-heading text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl"
			>
				Creator application
			</h2>
			<div class="mx-auto mt-6 flex max-w-3xl">
				<form
					onsubmit={handleSubmit}
					data-sveltekit-preload-data="off"
					class="relative flex w-full flex-col items-center justify-center gap-10 px-4 pb-12 pt-6"
				>
					<div class="flex w-full flex-col gap-4 sm:flex-row">
						<div class="flex w-full flex-col gap-1.5 sm:w-1/2">
							<Label for="firstName">First name</Label>
							<Input
								required
								type="text"
								id="firstName"
								placeholder="First name"
								bind:value={firstName}
							/>
						</div>
						<div class="flex w-full flex-col gap-1.5 sm:w-1/2">
							<Label for="lastName">Last name</Label>
							<Input
								required
								type="text"
								id="lastName"
								placeholder="Last name"
								bind:value={lastName}
							/>
						</div>
					</div>

					<div class="flex w-full flex-col gap-1.5">
						<Label for="email">Email address</Label>
						<Input
							required
							type="email"
							id="email"
							placeholder="email@domain.com"
							bind:value={email}
						/>
					</div>

					<div class="flex w-full flex-col gap-1.5">
						<Label for="platform">Primary platform and handle</Label>
						<Input
							required
							type="text"
							id="platform"
							placeholder="e.g. @handle on Instagram, TikTok, YouTube"
							bind:value={platform}
						/>
					</div>

					<div class="flex w-full flex-col gap-1.5">
						<Label>Approximate audience size</Label>
						<Select.Root type="single" bind:value={audienceSize}>
							<Select.Trigger>{audienceSizeTrigger}</Select.Trigger>
							<Select.Content>
								<Select.Group>
									{#each audienceSizeOptions as option}
										<Select.Item value={option.value} label={option.label}
											>{option.label}</Select.Item
										>
									{/each}
								</Select.Group>
							</Select.Content>
						</Select.Root>
					</div>

					<div class="flex w-full flex-col gap-1.5">
						<Label>Content niche</Label>
						<Select.Root type="single" bind:value={niche}>
							<Select.Trigger>{nicheTrigger}</Select.Trigger>
							<Select.Content>
								<Select.Group>
									{#each nicheOptions as option}
										<Select.Item value={option.value} label={option.label}
											>{option.label}</Select.Item
										>
									{/each}
								</Select.Group>
							</Select.Content>
						</Select.Root>
					</div>

					<div class="flex w-full flex-col gap-1.5">
						<Label for="why">Why Tasmania? Why Tassie Tours?</Label>
						<Textarea
							required
							id="why"
							placeholder="Tell us about yourself and why you want to create content in Tasmania..."
							class="min-h-32"
							bind:value={why}
						/>
					</div>

					<div class="relative mt-6 flex h-32 w-full items-center justify-center">
						{#if !submitted}
							<div
								class="absolute inset-0 flex justify-center"
								in:fly={{ x: -50, duration: 300 }}
								out:fly={{ x: -50, duration: 300 }}
							>
								{#if !loading}
									<Button
										type="submit"
										class="z-50 min-w-32 overflow-hidden rounded-full bg-tt-red p-3 text-center font-semibold text-background transition-all hover:bg-tt-red/80"
									>
										Submit application →
									</Button>
								{:else}
									<Button
										variant="outline"
										disabled
										class="z-50 flex min-w-32 overflow-hidden rounded-full bg-tt-red p-3 text-center font-semibold text-background transition-all"
									>
										<Loader2 class="mr-2 h-4 w-4 animate-spin" />
									</Button>
								{/if}
							</div>
						{:else}
							<div
								class="absolute inset-0 mx-auto flex w-full max-w-[500px] items-center justify-center"
								in:fly={{ x: 50, duration: 300, delay: 300 }}
								out:fly={{ x: -50, duration: 300 }}
							>
								<Alert.Root onclick={() => (submitted = false)} class="text-left hover:bg-accent">
									<CheckCircle class="h-4 w-4" />
									<Alert.Title>Application received!</Alert.Title>
									<Alert.Description class="text-wrap">
										We review every application personally and will be in touch within 5 business
										days.
									</Alert.Description>
								</Alert.Root>
							</div>
						{/if}
					</div>
				</form>
			</div>
		</div>
	</div>
</section>

<!-- Footer CTA -->
<section class="relative bg-[#194139] pb-32 pt-24">
	<div
		class="absolute -top-48 left-0 z-50 h-48 w-full"
		style="clip-path: polygon(27% 94%,66% 86%,100% 91%,100% 100%,0 100%,0 88%);"
	>
		<div class="h-full bg-[#194139]"></div>
	</div>
	<div
		class="absolute bottom-0 left-0 z-50 h-48 w-full"
		style="clip-path: polygon(27% 94%,66% 86%,100% 91%,100% 100%,0 100%,0 88%);"
	>
		<div class="h-full bg-white"></div>
	</div>
	<div class="px-6 sm:px-6 lg:px-8">
		<div class="mx-auto max-w-2xl text-center">
			<h2
				class="text-balance font-heading text-4xl font-semibold tracking-tight text-white sm:text-5xl"
			>
				Let's show the world what Tasmania looks like
			</h2>
			<p class="mx-auto mt-4 text-sm font-semibold uppercase tracking-widest text-white/70">
				Applications open now
			</p>
			<div class="mx-auto mt-8 flex flex-col items-center justify-center gap-4 lg:flex-row">
				<Button
					data-sveltekit-noscroll
					href="#apply"
					onclick={handleAnchorClick}
					class="pointer-events-auto rounded-full bg-tt-red px-6 font-semibold hover:bg-tt-red/90 lg:text-lg"
					>Apply to partner with us</Button
				>
			</div>
		</div>
	</div>
</section>
